/**
 * Pure aggregation of a bulk-upload's per-file outcomes.
 *
 * Upload is a per-file `Promise.allSettled`, so a batch is routinely part
 * succeeded. Duplicates are *skipped*, not failed: they belong in their own
 * dialog and must neither raise an error toast nor hold the batch open.
 *
 * Classification is by `error_code`, never by message text. Matching on the
 * prose is what let a same-key conflict escape the duplicate dialog, abort the
 * whole batch and leave every file — including the ones already stored —
 * selected, which is how one upload session became five retries (#1172).
 */

/**
 * Backend error codes that mean "already recorded", not "upload failed".
 *
 * `FILE_OBJECT_CONTENT_DUPLICATE` is the newest and arrives from a different
 * layer than the other four: `uq_file_objects_org_file_hash` makes duplicate
 * content per organization impossible, and the upload path refuses it *before*
 * the S3 write. It is listed here because it means exactly what the others do —
 * this organization already has this file — so it belongs in the duplicate
 * dialog. Omitting it would put it in the failure bucket, raise an error toast
 * and hold the whole batch open, which is precisely the #1172 regression this
 * module exists to prevent, reintroduced through a new code.
 *
 * `FILE_OBJECT_BLOB_CLAIMED` is deliberately NOT here. It is also a 409 from the
 * same pre-PUT stage, but it means "a different file already occupies this
 * storage key" — the user has to rename or move, so it is a genuine failure
 * needing action, not a skip.
 *
 * `FILE_OBJECT_NAME_RACE_LOST` is deliberately NOT here either, and it is new —
 * #39 Phase C added it for the case where a concurrent upload took this name in
 * this folder while ours was in flight (a 500 before, so every lost race filed a
 * Sentry issue). Nothing in this module enumerates the backend's
 * client-resolvable codes, so an unlisted 409 needs no change here: it renders
 * generically, in the failure bucket, which is where this one belongs.
 * Classified with
 * `FILE_OBJECT_BLOB_CLAIMED` rather than with the duplicates, because the two
 * buckets answer different questions and this one is emphatically not "already
 * recorded": another file took this name in this folder while the upload was in
 * flight, so **the user's bytes were not stored** and a retry will store them
 * (overwriting the file that won the race) — or they rename to keep both. Putting
 * it in the skip bucket would report a dropped upload as a stored one, which is
 * worse than holding the batch open. If a future change makes it a skip, it has
 * to make the upload succeed first.
 */
export const DUPLICATE_ERROR_CODES: readonly string[] = [
  'DUPLICATE_FILE_HASH',
  'DUPLICATE_FILE_NAME',
  'DUPLICATE_FLIGHT',
  'FILE_EXISTS',
  'FILE_OBJECT_CONTENT_DUPLICATE',
];

/**
 * Warning codes meaning "stored, but a feature will not work on this file until
 * you act" — as opposed to the merely informational ones.
 *
 * The split exists because these are not the same kind of message.
 * `FLIGHT_HOURS_NOT_EXTRACTED` says something about the log that was just sent:
 * nothing to do, nothing to fix. `FILE_NOT_REGISTERED` says the file is outside
 * the Files folders, so sharing and moving are unavailable until it is
 * re-uploaded (#1239). Rendering both with the same weight would either shout
 * about a non-event or bury an instruction.
 *
 * Classification lives here, keyed on the code, for the same reason
 * `DUPLICATE_ERROR_CODES` does: matching on prose is what caused #1172, and
 * these messages are user-facing copy that will be reworded by someone who has
 * no reason to grep the frontend. An unrecognised code degrades to
 * informational — the safe direction, since it is still shown, just without the
 * emphasis.
 */
export const ACTION_REQUIRED_WARNING_CODES: readonly string[] = ['FILE_NOT_REGISTERED'];

/** One `{code, message}` notice from a successful upload's response body. */
export interface UploadWarning {
  code: string;
  message: string;
}

/** A warning bound to the file it came from, ready to render. */
export interface FileWarning extends UploadWarning {
  fileName: string;
  /** True when the user has to act; see `ACTION_REQUIRED_WARNING_CODES`. */
  actionRequired: boolean;
}

/** Error thrown by `apiService.uploadFile`, carrying the problem+json code. */
export interface UploadError extends Error {
  fileName?: string;
  errorCode?: string;
}

export interface DuplicateFile {
  fileName: string;
  message: string;
}

export interface UploadSummary {
  uploadedCount: number;
  /** Already-recorded files — reported in the duplicate dialog, not as errors. */
  duplicates: DuplicateFile[];
  /** Genuine failures — the first one is surfaced to the user. */
  failed: UploadError[];
  /**
   * Caveats on files that DID upload (#1253).
   *
   * Deliberately not part of `failed` and deliberately absent from
   * `failedIndices`: the file is stored, so holding it selected for retry would
   * tell the user to upload it again when there is nothing to redo — the shape
   * of the #1172 regression, arrived at from the other direction.
   */
  warnings: FileWarning[];
  /**
   * Positions of the genuine failures in the batch — these stay selected so the
   * user can retry. Positions, not names: two files in one batch can share a
   * name (same log dragged from two folders), and filtering by name would hold
   * an already-stored file selected alongside its failed namesake.
   * `Promise.allSettled` preserves input order, so index i is files[i].
   */
  failedIndices: number[];
}

/** What an upload batch did, reported back to the uploader UI. */
export interface UploadOutcome {
  /** Indices into the `files` array that was passed to the upload call. */
  failedIndices: number[];
}

export function isDuplicateError(reason: unknown): boolean {
  const code = (reason as UploadError | null | undefined)?.errorCode;
  return typeof code === 'string' && DUPLICATE_ERROR_CODES.includes(code);
}

export function isActionRequiredWarning(code: string): boolean {
  return ACTION_REQUIRED_WARNING_CODES.includes(code);
}

/**
 * Pull the `{code, message}` notices off one fulfilled upload response.
 *
 * Tolerant of a bare string, and that is not defensive habit: `warnings` is a
 * channel three separate branches write to, and one of them may reach main
 * before being converted to the object shape. A string that arrived here would
 * otherwise be silently dropped — the precise failure #1253 exists to end — so
 * it is accepted and treated as informational, which is the safe default for a
 * notice whose severity cannot be established.
 */
function readWarnings(value: unknown): UploadWarning[] {
  const raw = (value as { warnings?: unknown } | null | undefined)?.warnings;
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((entry): UploadWarning[] => {
    if (typeof entry === 'string') return [{ code: '', message: entry }];
    const candidate = entry as Partial<UploadWarning> | null;
    if (typeof candidate?.message !== 'string') return [];
    return [
      {
        code: typeof candidate.code === 'string' ? candidate.code : '',
        message: candidate.message,
      },
    ];
  });
}

/**
 * One line for a toast, from the warnings of one severity.
 *
 * Deliberately **not** translated, and the reason is worth stating: the
 * messages it wraps are generated server-side in English and are not
 * translated either, so a localized "(+2 more)" glued to English prose would be
 * a costume rather than a translation. Making these localizable is a
 * backend-side change (send a code and let the client own the copy), which the
 * `code` field now makes possible but which is not this change.
 */
export function formatFileWarnings(warnings: readonly FileWarning[]): string {
  if (warnings.length === 0) return '';
  const [first, ...rest] = warnings;
  const head = `${first.fileName}: ${first.message}`;
  return rest.length === 0 ? head : `${head} (+${rest.length} more)`;
}

export function summarizeUploadResults(
  results: readonly PromiseSettledResult<unknown>[],
  unknownFileNameLabel: string,
  /**
   * The batch's file names, index-aligned with `results`. `Promise.allSettled`
   * preserves input order, so index i is files[i] — the same property
   * `failedIndices` already relies on. Optional so existing callers compile,
   * but a caller that omits it labels every warning "unknown file", which for a
   * multi-file batch is a warning the user cannot act on.
   */
  fileNames?: readonly string[]
): UploadSummary {
  const rejected = results
    .map((result, index) => ({ result, index }))
    .filter((entry) => entry.result.status === 'rejected')
    .map((entry) => ({
      reason: (entry.result as PromiseRejectedResult).reason as UploadError,
      index: entry.index,
    }));

  const duplicates = rejected.filter((entry) => isDuplicateError(entry.reason));
  const failed = rejected.filter((entry) => !isDuplicateError(entry.reason));

  // From the FULFILLED results — a warning belongs to an upload that worked.
  const warnings: FileWarning[] = results.flatMap((result, index) =>
    result.status === 'fulfilled'
      ? readWarnings(result.value).map((warning) => ({
          ...warning,
          fileName: fileNames?.[index] || unknownFileNameLabel,
          actionRequired: isActionRequiredWarning(warning.code),
        }))
      : []
  );

  return {
    uploadedCount: results.length - rejected.length,
    duplicates: duplicates.map(({ reason }) => ({
      fileName: reason?.fileName || unknownFileNameLabel,
      message: reason?.message ?? '',
    })),
    failed: failed.map(({ reason }) => reason),
    warnings,
    failedIndices: failed.map(({ index }) => index),
  };
}

/**
 * Which of the currently-selected entries survive an upload batch.
 *
 * Only genuine failures stay — succeeded and duplicate-skipped files are
 * cleared. Extracted so the rule is pinned by a test: the batch-abort bug it
 * replaces was invisible until a real upload session hit it (#1172).
 */
export function keepFailedSelections<T>(selected: readonly T[], failedIndices: number[]): T[] {
  const failed = new Set(failedIndices);
  return selected.filter((_, index) => failed.has(index));
}
